import { assertSafeName, describeToken, exprParseError, tokenize } from './lexer.js';
/** 05 §13: AST ≤ 5,000 nodes, nesting depth ≤ 64. */
export const MAX_AST_NODES = 5000;
export const MAX_PARSE_DEPTH = 64;
/** 05 §14 / 03 §7: parse cache keyed by source string, shared across engines. */
const CACHE_LIMIT = 2000;
/** Binary operator tables, index = precedence level (0 = loosest). */
const LEVEL_NOT = 2;
const BINARY_LEVELS = [
    new Map([
        ['or', 'or'],
        ['||', 'or'],
    ]),
    new Map([
        ['and', 'and'],
        ['&&', 'and'],
    ]),
    null, // LEVEL_NOT — prefix, handled by `notLevel()`
    new Map([
        ['=', '='],
        ['==', '='],
        ['!=', '!='],
        ['<', '<'],
        ['<=', '<='],
        ['>', '>'],
        ['>=', '>='],
    ]),
    new Map([
        ['+', '+'],
        ['-', '-'],
    ]),
    new Map([
        ['*', '*'],
        ['/', '/'],
        ['div', '/'],
        ['mod', 'mod'],
        ['%', 'mod'],
    ]),
];
/**
 * ODK's hyphenated names map to camelCase core names (spine §5). The documented
 * six (`string-length`, `count-selected`, `selected-at`, `indexed-repeat`,
 * `format-date`, `date-diff`) all follow the same mechanical rule, so one
 * transform covers them plus XPath spellings such as `starts-with`. Names that
 * do not exist after normalisation are rejected later, at form load, as
 * `RASD_EXPR_UNKNOWN_FUNCTION` — the parser is not the function registry.
 */
function normaliseFunctionName(name) {
    if (!name.includes('-'))
        return name;
    const parts = name.split('-');
    let out = parts[0] ?? '';
    for (let i = 1; i < parts.length; i++) {
        const p = parts[i] ?? '';
        out += p.charAt(0).toUpperCase() + p.slice(1);
    }
    return out;
}
/** The operator key a token contributes to the binary tables, if any. */
function opKey(t) {
    if (t.type === 'punct')
        return t.value;
    if (t.type === 'kw')
        return t.name;
    return undefined;
}
class Parser {
    src;
    toks;
    pos = 0;
    nodes = 0;
    depth = 0;
    constructor(src) {
        this.src = src;
        this.toks = tokenize(src);
    }
    parse() {
        const e = this.expr();
        const t = this.peek();
        if (t.type !== 'eof') {
            throw this.fail(t, 'end of expression');
        }
        return e;
    }
    // -- plumbing -------------------------------------------------------------
    peek() {
        const t = this.toks[this.pos];
        // Unreachable: the stream always ends with `eof` and we never advance past it.
        return t ?? { type: 'eof', o: this.src.length, end: this.src.length };
    }
    advance() {
        const t = this.peek();
        if (t.type !== 'eof')
            this.pos++;
        return t;
    }
    fail(t, expected) {
        // `not` in an operand position is the one parse error a form author is
        // almost certain to hit and almost certain to misread. docs/05 §6 makes
        // `not` deliberately LOOSER than comparison — so `not ${a} = 1` means
        // `not (${a} = 1)`, which is the useful reading — and the price is that
        // `${a} = not(${b})` cannot parse: after `=` the parser wants something
        // that binds tighter than `not`.
        //
        // Every ODK form survives this, because the XLSForm importer emits the
        // parenthesised form (`${b} = (not ${a})`) directly. It is the person
        // typing an expression by hand who gets stuck, and "expected an
        // expression" tells them nothing about the fix.
        if (t.type === 'kw' && t.name === 'not' && expected === 'an expression') {
            return exprParseError(this.src, t.o, `\`not\` binds looser than comparison (docs/05 §6), so it cannot appear here`, `a parenthesised operand — write \`(not …)\` instead of \`not …\``);
        }
        return exprParseError(this.src, t.o, `unexpected ${describeToken(t)}`, expected);
    }
    isPunct(t, value) {
        return t.type === 'punct' && t.value === value;
    }
    expectPunct(value) {
        const t = this.peek();
        if (!this.isPunct(t, value))
            throw this.fail(t, `'${value}'`);
        return this.advance();
    }
    node(n) {
        if (++this.nodes > MAX_AST_NODES) {
            throw exprParseError(this.src, n.o, `expression exceeds ${MAX_AST_NODES} AST nodes`);
        }
        return n;
    }
    /** Entry point for every nested expression; also the depth guard. */
    expr() {
        if (++this.depth > MAX_PARSE_DEPTH) {
            throw exprParseError(this.src, this.peek().o, `expression nests deeper than ${MAX_PARSE_DEPTH}`);
        }
        const e = this.ternary();
        this.depth--;
        return e;
    }
    // -- grammar --------------------------------------------------------------
    ternary() {
        const test = this.binary(0);
        if (!this.isPunct(this.peek(), '?'))
            return test;
        this.advance();
        const a = this.expr();
        this.expectPunct(':');
        const b = this.expr();
        return this.node({ k: 'cond', test, a, b, o: test.o });
    }
    binary(level) {
        if (level >= BINARY_LEVELS.length)
            return this.unary();
        if (level === LEVEL_NOT)
            return this.notLevel(level);
        const table = BINARY_LEVELS[level];
        if (!table)
            return this.binary(level + 1);
        let left = this.binary(level + 1);
        for (;;) {
            const t = this.peek();
            const key = opKey(t);
            const op = key === undefined ? undefined : table.get(key);
            if (op === undefined)
                break;
            this.advance();
            const right = this.binary(level + 1);
            left = this.node({ k: 'bin', op, a: left, b: right, o: left.o });
        }
        return left;
    }
    /**
     * `Not ::= ('not' | '!') Not | Comparison`.
     * `not(x)` from XLSForm parses here too: `not` is a keyword, so `(x)` is just a
     * parenthesised operand — same node, same meaning as the function form.
     */
    notLevel(level) {
        const t = this.peek();
        if ((t.type === 'kw' && t.name === 'not') || this.isPunct(t, '!')) {
            this.advance();
            const a = this.notLevel(level);
            return this.node({ k: 'un', op: 'not', a, o: t.o });
        }
        return this.binary(level + 1);
    }
    unary() {
        const t = this.peek();
        if (this.isPunct(t, '-')) {
            this.advance();
            const a = this.unary();
            return this.node({ k: 'un', op: '-', a, o: t.o });
        }
        return this.postfix();
    }
    postfix() {
        let a = this.primary();
        for (;;) {
            const t = this.peek();
            if (this.isPunct(t, '.')) {
                this.advance();
                const n = this.peek();
                if (n.type !== 'ident' || n.hyphenated)
                    throw this.fail(n, 'a property name');
                assertSafeName(n.name, this.src, n.o);
                this.advance();
                a = this.node({ k: 'member', a, name: n.name, o: a.o });
            }
            else if (this.isPunct(t, '[')) {
                this.advance();
                const i = this.expr();
                this.expectPunct(']');
                a = this.node({ k: 'index', a, i, o: a.o });
            }
            else {
                return a;
            }
        }
    }
    primary() {
        const t = this.peek();
        switch (t.type) {
            case 'num':
                this.advance();
                return this.node({ k: 'lit', v: t.num, o: t.o });
            case 'str':
                this.advance();
                return this.node({ k: 'lit', v: t.str, o: t.o });
            case 'ref':
                this.advance();
                return this.node({
                    k: 'ref',
                    path: t.ref.path,
                    up: t.ref.up,
                    root: t.ref.root,
                    meta: t.ref.meta,
                    o: t.o,
                });
            case 'kw':
                return this.keywordPrimary(t);
            case 'ident': {
                this.advance();
                if (this.isPunct(this.peek(), '(')) {
                    this.advance();
                    const args = this.argList();
                    return this.node({ k: 'call', name: normaliseFunctionName(t.name), args, o: t.o });
                }
                // A hyphenated name can only be lexed in call position, so this is
                // defensive; a bare identifier is a choice column (legal in
                // `choiceFilter` only — the engine, not the parser, enforces that).
                if (t.hyphenated)
                    throw this.fail(t, 'a function call');
                assertSafeName(t.name, this.src, t.o);
                return this.node({ k: 'col', name: t.name, o: t.o });
            }
            case 'punct':
                if (t.value === '(') {
                    this.advance();
                    const e = this.expr();
                    this.expectPunct(')');
                    return e;
                }
                if (t.value === '[') {
                    this.advance();
                    const items = [];
                    if (!this.isPunct(this.peek(), ']')) {
                        for (;;) {
                            items.push(this.expr());
                            if (!this.isPunct(this.peek(), ','))
                                break;
                            this.advance();
                        }
                    }
                    this.expectPunct(']');
                    return this.node({ k: 'arr', items, o: t.o });
                }
                if (t.value === '.') {
                    // A `.` that survived the lexer's number rule is the owner value.
                    this.advance();
                    return this.node({ k: 'dot', o: t.o });
                }
                throw this.fail(t, 'an expression');
            default:
                throw this.fail(t, 'an expression');
        }
    }
    keywordPrimary(t) {
        if (t.name === 'true' || t.name === 'false') {
            this.advance();
            // `true()` / `false()` are literals, not calls (05 §3).
            if (this.isPunct(this.peek(), '(')) {
                this.advance();
                this.expectPunct(')');
            }
            return this.node({ k: 'lit', v: t.name === 'true', o: t.o });
        }
        if (t.name === 'null') {
            this.advance();
            return this.node({ k: 'lit', v: null, o: t.o });
        }
        throw this.fail(t, 'an expression');
    }
    argList() {
        const args = [];
        if (this.isPunct(this.peek(), ')')) {
            this.advance();
            return args;
        }
        for (;;) {
            args.push(this.expr());
            const t = this.peek();
            if (this.isPunct(t, ',')) {
                this.advance();
                continue;
            }
            this.expectPunct(')');
            return args;
        }
    }
}
/** Freezes a whole AST in place. Cached ASTs are shared, so they must be immutable. */
function freezeAst(node) {
    switch (node.k) {
        case 'lit':
        case 'dot':
        case 'col':
            break;
        case 'ref':
            for (const seg of node.path)
                Object.freeze(seg);
            Object.freeze(node.path);
            break;
        case 'un':
            freezeAst(node.a);
            break;
        case 'bin':
            freezeAst(node.a);
            freezeAst(node.b);
            break;
        case 'cond':
            freezeAst(node.test);
            freezeAst(node.a);
            freezeAst(node.b);
            break;
        case 'call':
            for (const arg of node.args)
                freezeAst(arg);
            Object.freeze(node.args);
            break;
        case 'arr':
            for (const item of node.items)
                freezeAst(item);
            Object.freeze(node.items);
            break;
        case 'member':
            freezeAst(node.a);
            break;
        case 'index':
            freezeAst(node.a);
            freezeAst(node.i);
            break;
    }
    return Object.freeze(node);
}
const cache = new Map();
/**
 * Parses REL source into a frozen AST.
 *
 * @throws RasdError `RASD_EXPR_PARSE` with `details: { source, offset, expected? }`.
 *
 * Results are memoised by source string (`opts.cache`, default `true`). The map
 * is bounded at 2,000 entries with FIFO eviction — a plain LRU would need a
 * touch on every read, and REL sources are short-lived per form anyway.
 */
export function parseExpression(src, opts) {
    const useCache = opts?.cache !== false;
    if (useCache) {
        const hit = cache.get(src);
        if (hit !== undefined)
            return hit;
    }
    const ast = freezeAst(new Parser(src).parse());
    if (useCache) {
        if (cache.size >= CACHE_LIMIT) {
            const oldest = cache.keys().next();
            if (!oldest.done)
                cache.delete(oldest.value);
        }
        cache.set(src, ast);
    }
    return ast;
}
/** Empties the parse cache. Exported for tests and for hosts that hot-reload forms. */
export function clearExpressionCache() {
    cache.clear();
}
//# sourceMappingURL=parser.js.map